import React, { FC, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Check from '@mui/icons-material/Check';
import DataTable from '../../dataTable';
import ErrorMessage from '../../layout/ErrorMessage';
import IssueReportsFilterBar from '../components/IssueReportsFilterBar';
import IssueCompleteModal from '../components/IssueCompleteModal';
import LoadingWrapper from '../../layout/LoadingWrapper';
import PageHeader from '../../layout/PageHeader';
import StatusCell from '../../layout/StatusCell';

import IssueReport from '../model';

import axios from '../../utils/axios.utils';

interface FilterValues {
  status_eq?: string;
  reportable_type_eq?: string;
}

const IssueReportListPage: FC = () => {
  let history = useHistory();

  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const defaultType = params.get('default_type');

  let [issueCompleteModalOpen, setIssueCompleteModalOpen] = useState<boolean>(false);
  let [issueReports, setIssueReports] = useState<IssueReport>();
  let [issueReportToMarkId, setIssueReportToMarkId] = useState<string>();
  let [errorMessage, setErrorMessage] = useState<string>('');
  let [filterValues, setFilterValues] = useState<any>({ status_eq: 'Submitted', reportable_type_eq: 'all' });
  let [page, setPage] = useState<number>(1);
  let [query, setQuery] = useState<string>();
  let [sort, setSort] = useState<string>();
  let [loading, setLoading] = useState<boolean>(false);
  let [totalRecords, setTotalRecords] = useState<number>();

  function toRansackParams(params: FilterValues) {
    const parts: string[] = [];

    for (const key in params) {
      const value = params[key];

      if (Array.isArray(value)) {
        value.forEach((v) => {
          parts.push(`ransack[${key}][]=${encodeURIComponent(v)}`);
        });
      } else if (value !== undefined && value !== null && value !== '') {
        parts.push(`ransack[${key}]=${encodeURIComponent(value)}`);
      }
    }

    return parts.join('&');
  }

  const fetchIssueReports = async (
    controllerName: string,
    searchFilters: any,
    inputPage: number,
    query?: string,
    sort?: string,
  ): Promise<void> => {
    try {
      setLoading(true);

      const searchParams = toRansackParams(searchFilters);

      let urlWithParams = `${controllerName}?${searchParams}&page=${inputPage}`;

      if (query) {
        urlWithParams = urlWithParams.concat(`&query=${query}`);
      }

      if (sort) {
        urlWithParams = urlWithParams.concat(`&sort=${sort}`);
      }

      const response = await axios.get<string, any>(urlWithParams);
      setIssueReports(response?.data?.result || {});
      setTotalRecords(response?.data?.total_records);
    } catch {
      setErrorMessage('Something Went Wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let updatedFilterValues = { ...filterValues };

    if (defaultType) {
      updatedFilterValues.reportable_type_eq = defaultType;
      setFilterValues(updatedFilterValues);
    }

    fetchIssueReports('issue_reports', updatedFilterValues, 1);
  }, [defaultType]);

  const handleReviewClicked = (row) => {
    setIssueReportToMarkId(row?.id);
    setIssueCompleteModalOpen(true);
  };

  const reviewRequestsColumns = [
    {
      name: 'Type',
      selector: 'reportable_type',
      grow: '0.5',
      sortable: true,
    },
    {
      name: 'Submitted',
      selector: 'updated_at_with_time',
      sortable: true,
    },
    {
      name: 'Tech',
      selector: 'user_name',
      sortable: true,
    },
    {
      name: 'Customer',
      selector: 'company_name',
      grow: 1,
      sortable: true,
    },
    {
      name: 'Message',
      selector: 'message',
      grow: 2,
      sortable: true,
    },
    {
      name: 'Status',
      selector: 'status',
      grow: 1,
      sortable: true,
      cell: (row) => <StatusCell status={row.status} />,
    },
    {
      name: 'Actions',
      grow: 0.5,
      cell: (row) => (
        <Button variant="contained" color="primary" onClick={() => handleReviewClicked(row)}>
          <Check />
        </Button>
      ),
    },
  ];

  return (
    <LoadingWrapper isLoading={loading} message="Loading">
      <IssueCompleteModal
        issueReportToMarkId={issueReportToMarkId}
        issueCompleteModalOpen={issueCompleteModalOpen}
        setIssueCompleteModalOpen={setIssueCompleteModalOpen}
        setIssueReports={setIssueReports}
      />
      <Grid container style={{ padding: '1% 2%' }}>
        <Grid item xs={12}>
          <Typography variant="h4" component="h4">
            Issue Reports
          </Typography>
        </Grid>
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <IssueReportsFilterBar
            filterValues={filterValues}
            setFilterValues={setFilterValues}
            fetchIssueReports={fetchIssueReports}
            setSortParam={setSort}
            searchQuery={query}
            setSearchQuery={setQuery}
            setPage={setPage}
          />
        </Grid>
        <Grid item xs={12} style={{ marginBottom: 15 }}>
          <ErrorMessage message={errorMessage} />
        </Grid>
        <Grid item xs={12}>
          <DataTable
            title=""
            columns={reviewRequestsColumns}
            data={issueReports}
            onRowClicked={(row: any) => history.push(`/issue_reports/${row?.id}`)}
            striped
            dense
            highlightOnHover
            pointerOnHover
            pagination
            noHeader
          />
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default IssueReportListPage;
